import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CloudUpload,
  Download,
  Eye,
  FolderOpen,
  LayoutGrid,
  LayoutList,
  MoreVertical,
  Pencil,
  Plus,
  Share2,
  Trash2,
  Users,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import {
  compartilharRecurso,
  deleteArtefato,
  downloadArtefato,
  fetchArtefatoCounts,
  fetchArtefatos,
  fetchCompartilhamentos,
  fetchEscritorios,
  previewArtefato,
  removeCompartilhamento,
  updateArtefato,
  type Artefato,
  type CompartilhamentoInfo,
  type EscritorioResumo,
} from '../api/client';
import { ClienteCombobox } from '@/components/ClienteCombobox';
import { DocumentPreviewDialog } from '@/components/DocumentPreviewDialog';
import { UploadDocumentoDialog } from '@/components/UploadDocumentoDialog';
import { FileTypeIcon } from '@/components/ui/FileTypeIcon';
import { classifyError, notifyMutationError } from '../lib/errors';
import { useNotification } from '../context/NotificationProvider';
import { useAuth } from '../hooks/useAuth';
import { useDebounce } from '@/hooks/useDebounce';
import { useRigorDaBusca } from '@/hooks/useSmartSearch';
import { useOfflineMutation } from '@/hooks/useOfflineMutation';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useResourcePermissions } from '@/hooks/useResourcePermissions';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { queryPersistMeta } from '@/lib/pwa/queryPersistence';
import { usePaginatedQuery } from '@/hooks/usePaginatedQuery';
import { getCachedDocument, cacheDocument, removeDocument } from '@/lib/pwa/documentCache';
import { DataTablePagination } from '@/components/DataTablePagination';
import { useCompartilharNoEscritorio } from '@/components/escritorio/useCompartilharNoEscritorio';
import { SortableHeader } from '@/components/SortableHeader';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BarraDaLista } from '@/components/ui/barra-da-lista';
import { OrigemFilterBar } from '@/components/ui/OrigemFilterBar';
import { BuscaInteligente } from '@/components/ui/smart-search';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeader } from '@/components/ui/PageHeader';
import { Fab } from '@/components/ui/Fab';
import { Spinner } from '@/components/ui/spinner';
import { AbasDaLista } from '@/components/ui/abas-da-lista';
import { TagChips } from '@/components/ui/tag-chip';
import { TagsDoRegistro } from '@/components/tags/TagsDoRegistro';
import { useTagsNoCache } from '@/hooks/useTagsNoCache';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { formatFileSize } from '@/lib/formatters';
import { OrigemBadge } from '@/components/ui/OrigemBadge';
import { useAtuacao } from '@/lib/delegacao/atuacao';
import { cn } from '@/lib/utils';

// --- Utilitários ---

const ABAS: { value: string; label: string; contagem: 'total' | 'documentos' | 'imagens' | 'outros' }[] = [
  { value: 'todos', label: 'Todos', contagem: 'total' },
  { value: 'documentos', label: 'Documentos', contagem: 'documentos' },
  { value: 'imagens', label: 'Imagens', contagem: 'imagens' },
  { value: 'outros', label: 'Outros', contagem: 'outros' },
];

const tipoLabels: Record<string, string> = {
  DOCUMENTO: 'Documento',
  IMAGEM: 'Imagem',
  AUDIO: 'Áudio',
  VIDEO: 'Vídeo',
  OUTRO: 'Outro',
};

function TipoBadge({ tipo }: { tipo: string }) {
  return (
    <span className="inline-block text-xs px-2 py-0.5 rounded-full font-medium bg-muted text-muted-foreground">
      {tipoLabels[tipo] || tipo}
    </span>
  );
}


function isPreviewable(mime: string): boolean {
  return mime === 'application/pdf' || mime.startsWith('image/');
}

// --- Componente Principal ---

// Lista de documentos (todas as páginas e filtros), sob o prefixo `artefatos`.
const CACHES_DE_DOCUMENTO = [queryKeys.documentos.artefatos] as const;

function DocumentsPage() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();
  const { isOnline } = useOnlineStatus();

  // Estado da página
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    const saved = localStorage.getItem('documents-view-mode');
    if (saved === 'grid' || saved === 'list') return saved;
    // Default: grid no mobile, list no desktop
    return window.innerWidth < 640 ? 'grid' : 'list';
  });
  const changeViewMode = (mode: 'grid' | 'list') => {
    setViewMode(mode);
    localStorage.setItem('documents-view-mode', mode);
  };
  const [activeTab, setActiveTab] = useState('todos');
  const [searchQuery, setSearchQuery] = useState('');

  // Debounce da busca (300ms) para não disparar request a cada tecla
  const debouncedSearch = useDebounce(searchQuery, 300);
  // Filtro de origem
  const [origemFilter, setOrigemFilter] = useState<string | undefined>(undefined);
  // Filtro por tag (exata, server-side)
  // Filtro por tags (PLANO_TAGS D7), no servidor como o resto desta lista paginada.
  const [filtroTags, setFiltroTags] = useState<string[]>([]);
  const tagIds = filtroTags.length ? filtroTags : undefined;
  const tagsNoCache = useTagsNoCache(CACHES_DE_DOCUMENTO, 'etiquetas');
  const searchFilter = debouncedSearch.trim() || undefined;
  // A mesma busca inteligente do Acervo, feita no servidor (backend: repositories/busca_inteligente.py).
  const [rigor, setRigor] = useRigorDaBusca();
  const rigorDaBusca = searchFilter ? rigor : undefined;

  // Escritórios do usuário
  const { data: escritorios, error: escritoriosError } = useQuery<EscritorioResumo[]>({
    queryKey: ['escritorios'],
    queryFn: fetchEscritorios,
    meta: queryPersistMeta.nonEmpty,
  });
  const hasEscritorios = (escritorios && escritorios.length > 0) || !!escritoriosError;

  // Helpers de permissão por origem (centralizados no hook)
  const { canWrite: canWriteCheck, canShare: canShareCheck } = useResourcePermissions({
    escritorios, isAdmin: user?.role === 'admin', currentUserId: user?.id,
  });
  // Na conta de outra pessoa os documentos são só para ver e baixar.
  const atuacao = useAtuacao();
  const compartilharNoEscritorio = useCompartilharNoEscritorio();
  const canWrite = (doc: Artefato) => !atuacao && canWriteCheck(doc.origem, doc.escritorio_id);
  const canShare = (doc: Artefato) => !atuacao && canShareCheck(doc.origem, doc.escritorio_id, doc.usuario_id);

  // Mapeamento tab → filtro server-side de tipo
  const tipoFilter = activeTab === 'documentos' ? 'DOCUMENTO'
    : activeTab === 'imagens' ? 'IMAGEM'
    : undefined; // 'todos' e 'outros' não filtram server-side

  // Estado dos dialogs
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Estado do documento selecionado
  const [selectedDocument, setSelectedDocument] = useState<Artefato | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');

  // Estado da edição
  const [editDescricao, setEditDescricao] = useState('');

  // Estado de vincular cliente
  const [linkClienteDialogOpen, setLinkClienteDialogOpen] = useState(false);
  const [linkClienteDocId, setLinkClienteDocId] = useState<string | null>(null);
  const [linkClienteValue, setLinkClienteValue] = useState<string | null>(null);

  // Estado do compartilhamento
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [sharing, setSharing] = useState(false);
  const [shareList, setShareList] = useState<CompartilhamentoInfo[]>([]);
  const [loadingShares, setLoadingShares] = useState(false);

  // Query paginada server-side
  const {
    data: artefatosData,
    isLoading,
    error: artefatosError,
    dataUpdatedAt,
    pagination,
    sorting,
  } = usePaginatedQuery<Artefato>({
    queryKey: [
      ...queryKeys.documentos.artefatos,
      { tipo: tipoFilter, search: searchFilter, rigor: rigorDaBusca, tagIds, origem: origemFilter },
    ],
    meta: queryPersistMeta.nonEmpty,
    fetchFn: (params) =>
      fetchArtefatos({
        tipo: tipoFilter,
        search: searchFilter,
        rigor: rigorDaBusca,
        tag_ids: tagIds,
        origem: origemFilter,
        ...params,
      }),
    defaultSort: { orderBy: 'created_at', orderDir: 'desc' },
  });

  // Resetar página quando busca, rigor ou tags mudam
  useEffect(() => {
    pagination.resetPage();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFilter, rigorDaBusca, filtroTags]);

  const artefatos = useMemo(() => artefatosData?.items ?? [], [artefatosData]);

  // KPIs do topo — visão geral fixa, não reage ao search
  const { data: totalCounts, error: countsError } = useQuery({
    queryKey: [...queryKeys.documentos.artefatoCounts],
    queryFn: () => fetchArtefatoCounts(),
    meta: queryPersistMeta.nonEmpty,
  });

  // Contadores das abas — reagem ao filtro de busca
  const { data: filteredCounts, error: filteredCountsError } = useQuery({
    queryKey: [...queryKeys.documentos.artefatoCounts, { search: searchFilter, rigor: rigorDaBusca, tagIds, origem: origemFilter }],
    queryFn: () =>
      fetchArtefatoCounts({ search: searchFilter, rigor: rigorDaBusca, tag_ids: tagIds, origem: origemFilter }),
    enabled: !!(searchFilter || tagIds || origemFilter),
    meta: queryPersistMeta.nonEmpty,
  });

  const tabCounts = (searchFilter || tagIds || origemFilter) ? filteredCounts : totalCounts;
  // Quantos a aba ativa mostra (com a busca e os filtros aplicados).
  const contagemDaAba = tabCounts?.[ABAS.find((a) => a.value === activeTab)?.contagem ?? 'total'];

  // Mutations
  const deleteMutation = useOfflineMutation({
    mutationFn: deleteArtefato,
    outbox: {
      type: 'documento_deletar',
      getPayload: (id) => ({ method: 'DELETE', url: `/artefatos/${id}` }),
      getDescription: () => 'Mover documento para lixeira',
    },
    invalidateKeys: [[...queryKeys.documentos.artefatos]],
    onSuccess: () => {
      showSuccess('Documento movido para a lixeira');
      // Limpar cache offline do documento excluído
      if (selectedDocument) {
        removeDocument(selectedDocument.id).catch(() => {});
      }
      setDeleteDialogOpen(false);
      setSelectedDocument(null);
    },
    onQueued: () => {
      // Limpar cache offline mesmo quando a exclusão fica na fila
      if (selectedDocument) {
        removeDocument(selectedDocument.id).catch(() => {});
      }
      setDeleteDialogOpen(false);
      setSelectedDocument(null);
    },
    onError: () => {
      showError('Erro ao mover documento para a lixeira');
    },
  });

  const updateMutation = useOfflineMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { descricao?: string } }) =>
      updateArtefato(id, payload),
    outbox: {
      type: 'documento_editar',
      getPayload: ({ id, payload }) => ({ method: 'PUT', url: `/artefatos/${id}`, body: payload }),
      getDescription: () => 'Editar documento',
    },
    invalidateKeys: [[...queryKeys.documentos.artefatos]],
    onSuccess: () => {
      showSuccess('Documento atualizado com sucesso');
      setEditDialogOpen(false);
      setSelectedDocument(null);
    },
    onQueued: () => {
      setEditDialogOpen(false);
      setSelectedDocument(null);
    },
    onError: () => {
      showError('Erro ao atualizar documento');
    },
  });

  // Handlers
  const handleDownload = async (doc: Artefato) => {
    try {
      // Offline: tentar cache
      if (!isOnline) {
        const cached = await getCachedDocument(doc.id);
        if (cached) {
          const blob = await cached.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = doc.nome;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          return;
        }
        showError('Documento não disponível offline');
        return;
      }

      // Online: fluxo normal + cache em background
      const { download_url } = await downloadArtefato(doc.id);
      window.open(download_url, '_blank');
      // Cache para uso offline
      cacheDocument(doc.id, download_url, {
        fileName: doc.nome,
        mimeType: doc.mime,
        size: doc.tamanho,
      }).catch(() => {}); // não-crítico
    } catch {
      showError('Erro ao gerar link de download');
    }
  };

  const handlePreview = async (doc: Artefato) => {
    if (!isPreviewable(doc.mime)) {
      handleDownload(doc);
      return;
    }
    try {
      // Tentar cache primeiro
      const cached = await getCachedDocument(doc.id);
      if (cached) {
        const blob = await cached.blob();
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
        setSelectedDocument(doc);
        setPreviewDialogOpen(true);
        return;
      }

      // Offline e não cacheado
      if (!isOnline) {
        showError('Documento não disponível offline');
        return;
      }

      // Online: buscar, cachear e exibir
      const { download_url } = await previewArtefato(doc.id);
      // Cache em background para uso offline
      cacheDocument(doc.id, download_url, {
        fileName: doc.nome,
        mimeType: doc.mime,
        size: doc.tamanho,
      }).catch(() => {}); // não-crítico

      setPreviewUrl(download_url);
      setSelectedDocument(doc);
      setPreviewDialogOpen(true);
    } catch {
      showError('Erro ao gerar visualização');
    }
  };

  const handleOpenEdit = (doc: Artefato) => {
    setSelectedDocument(doc);
    setEditDescricao(doc.descricao || '');
    setEditDialogOpen(true);
  };

  const handleSaveEdit = () => {
    if (!selectedDocument) return;
    updateMutation.mutate({
      id: selectedDocument.id,
      payload: {
        descricao: editDescricao,
      },
    });
  };

  const handleOpenShare = async (doc: Artefato) => {
    setSelectedDocument(doc);
    setShareEmail('');
    setShareDialogOpen(true);
    setLoadingShares(true);
    try {
      const shares = await fetchCompartilhamentos('ARTEFATO', doc.id);
      setShareList(shares);
    } catch (err) {
      notifyMutationError(err, showError);
      setShareDialogOpen(false);
      setShareList([]);
    } finally {
      setLoadingShares(false);
    }
  };

  const handleShare = async () => {
    if (!selectedDocument || !shareEmail.trim()) return;
    setSharing(true);
    try {
      await compartilharRecurso({ recurso_tipo: 'ARTEFATO', recurso_id: selectedDocument.id, email: shareEmail });
      showSuccess('Documento compartilhado com sucesso');
      setShareEmail('');
      const shares = await fetchCompartilhamentos('ARTEFATO', selectedDocument.id);
      setShareList(shares);
    } catch (err) {
      notifyMutationError(err, showError);
    } finally {
      setSharing(false);
    }
  };

  const handleRevokeShare = async (shareId: string) => {
    try {
      await removeCompartilhamento(shareId);
      showSuccess('Compartilhamento revogado');
      if (selectedDocument) {
        const shares = await fetchCompartilhamentos('ARTEFATO', selectedDocument.id);
        setShareList(shares);
      }
    } catch {
      showError('Erro ao revogar compartilhamento');
    }
  };

  const handleOpenLinkCliente = (doc: Artefato) => {
    setLinkClienteDocId(doc.id);
    setLinkClienteValue(null);
    setLinkClienteDialogOpen(true);
  };

  const handleConfirmLinkCliente = async () => {
    if (!linkClienteDocId || !linkClienteValue) return;
    try {
      await updateArtefato(linkClienteDocId, { cliente_id: linkClienteValue });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.documentos.artefatos] });
      showSuccess('Cliente vinculado ao documento');
      setLinkClienteDialogOpen(false);
    } catch (err) {
      notifyMutationError(err, showError);
    }
  };

  const handleUnlinkCliente = async (doc: Artefato) => {
    try {
      await updateArtefato(doc.id, { cliente_id: null });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.documentos.artefatos] });
      showSuccess('Cliente desvinculado do documento');
    } catch (err) {
      notifyMutationError(err, showError);
    }
  };

  const handleOpenDelete = (doc: Artefato) => {
    setSelectedDocument(doc);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedDocument) {
      deleteMutation.mutate(selectedDocument.id);
    }
  };

  // Handler de troca de tab — reseta paginação
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    pagination.resetPage();
  };

  // Filtro client-side apenas para tab "outros" (exclui DOCUMENTO e IMAGEM)
  const filteredDocuments = activeTab === 'outros'
    ? artefatos.filter((doc) => !['DOCUMENTO', 'IMAGEM'].includes(doc.tipo))
    : artefatos;


  return (
    <div className="space-y-6">
        <PageHeader
          icon={<FolderOpen />}
          title="Documentos"
          subtitle="Gerencie todos os documentos do seu escritório"
          dataUpdatedAt={dataUpdatedAt}
          actions={
            !atuacao && (
              <Button onClick={() => setUploadDialogOpen(true)} className="hidden sm:inline-flex">
                <CloudUpload className="mr-2 h-4 w-4" />
                Upload
              </Button>
            )
          }
        />

        {!atuacao && (
          <Fab icon={<Plus className="h-6 w-6" />} label="Upload de documento" onClick={() => setUploadDialogOpen(true)} />
        )}

        {(artefatosError || countsError || filteredCountsError || escritoriosError) && (
          <Alert variant="destructive">
            <AlertDescription>{classifyError(artefatosError || countsError || filteredCountsError || escritoriosError).message}</AlertDescription>
          </Alert>
        )}

        {/* Barra da lista (docs/PLANO_BARRA_DE_LISTA.md): abas à esquerda, a busca no resto. */}
        <BarraDaLista
          abas={
            <AbasDaLista
              nome="Tipo"
              value={activeTab}
              onValueChange={handleTabChange}
              abas={ABAS.map((a) => ({ value: a.value, label: a.label, contagem: activeTab === a.value ? contagemDaAba : undefined }))}
            />
          }
          busca={
            <BuscaInteligente
              value={searchQuery}
              onChange={setSearchQuery}
              strictness={rigor}
              onStrictnessChange={setRigor}
              placeholder="Buscar por nome, descrição ou tag…"
              contagem={
                searchFilter && contagemDaAba !== undefined
                  ? `${contagemDaAba} ${contagemDaAba === 1 ? 'documento' : 'documentos'}`
                  : undefined
              }
            />
          }
          filtros={
            <>
              {hasEscritorios && (
                <OrigemFilterBar
                  escritorios={escritorios ?? []}
                  value={origemFilter}
                  onChange={(v) => {
                    setOrigemFilter(v);
                    pagination.resetPage();
                  }}
                  includeCompartilhados
                />
              )}
            </>
          }
          filtrosAtivos={origemFilter ? 1 : 0}
          filtroDeTags={{ value: filtroTags, onChange: setFiltroTags }}
          acoes={
            <>
            {totalCounts && (
              <span className="hidden whitespace-nowrap text-xs text-muted-foreground sm:inline">
                {formatFileSize(totalCounts.storage_bytes)} utilizados
              </span>
            )}
            <div className="flex shrink-0 rounded-md border border-input" role="group" aria-label="Exibição">
              <Button
                variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                size="icon"
                className="h-11 w-11 rounded-r-none sm:h-9 sm:w-9"
                aria-label="Ver em lista"
                aria-pressed={viewMode === 'list'}
                onClick={() => changeViewMode('list')}
              >
                <LayoutList className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                size="icon"
                className="h-11 w-11 rounded-l-none sm:h-9 sm:w-9"
                aria-label="Ver em grade"
                aria-pressed={viewMode === 'grid'}
                onClick={() => changeViewMode('grid')}
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
            </div>
            </>
          }
        />

        {/* Conteúdo principal. Em grade, no celular, sem a moldura: cada documento já é um
            cartão (docs/PLANO_RESPONSIVIDADE_MOBILE.md §3.4). */}
        <Card className={cn(viewMode === 'grid' && 'max-sm:border-0 max-sm:bg-transparent max-sm:shadow-none')}>
              {isLoading ? (
                <div className="flex justify-center py-12">
                  <Spinner size={32} label="Carregando documentos" />
                </div>
              ) : filteredDocuments.length === 0 ? (
                <EmptyState
                  icon={<FolderOpen />}
                  title="Nenhum documento encontrado"
                  description={atuacao ? 'Nenhum documento nesta conta ainda.' : 'Faça upload de um documento para começar'}
                />

              ) : viewMode === 'list' ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableHeader
                        field="nome"
                        label="Nome"
                        currentField={sorting.orderBy}
                        currentDir={sorting.orderDir}
                        onToggle={sorting.toggleSort}
                      />
                      <TableHead className="hidden sm:table-cell">Tipo</TableHead>
                      <SortableHeader
                        field="tamanho"
                        label="Tamanho"
                        currentField={sorting.orderBy}
                        currentDir={sorting.orderDir}
                        onToggle={sorting.toggleSort}
                        className="hidden sm:table-cell"
                      />
                      <SortableHeader
                        field="created_at"
                        label="Data"
                        currentField={sorting.orderBy}
                        currentDir={sorting.orderDir}
                        onToggle={sorting.toggleSort}
                        className="hidden sm:table-cell"
                      />
                      <TableHead className="hidden sm:table-cell">Tags</TableHead>
                      <TableHead className="text-right w-[120px]">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDocuments.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <FileTypeIcon mime={doc.mime} />
                            <div className="min-w-0">
                              <span
                                className="block cursor-pointer [overflow-wrap:anywhere] text-sm font-medium hover:underline"
                                onClick={() => handlePreview(doc)}
                              >{doc.nome}</span>
                              {(doc.descricao || (doc.etiquetas && doc.etiquetas.length > 0) || (doc.origem && doc.origem !== 'pessoal')) && (
                                <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
                                  <OrigemBadge item={doc} />
                                  {doc.descricao && (
                                    <span className="min-w-0 shrink break-words text-xs text-muted-foreground">
                                      {doc.descricao}
                                    </span>
                                  )}
                                  <TagChips tags={doc.etiquetas} className="shrink-0 sm:hidden" />
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <TipoBadge tipo={doc.tipo} />
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <span className="text-sm text-muted-foreground">
                            {formatFileSize(doc.tamanho)}
                          </span>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <span className="text-sm text-muted-foreground">
                            {new Date(doc.created_at).toLocaleDateString('pt-BR')}
                          </span>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <TagsDoRegistro
                            recurso="artefatos"
                            registroId={doc.id}
                            identificador={doc.nome}
                            tags={doc.etiquetas}
                            noCache={tagsNoCache}
                            max={2}
                            className="max-w-[200px]"
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => handlePreview(doc)}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Visualizar</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => handleDownload(doc)}
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Download</TooltipContent>
                            </Tooltip>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handlePreview(doc)}>
                                  <Eye className="mr-2 h-4 w-4" />
                                  Visualizar
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDownload(doc)}>
                                  <Download className="mr-2 h-4 w-4" />
                                  Download
                                </DropdownMenuItem>
                                {canWrite(doc) && (
                                  <DropdownMenuItem onClick={() => handleOpenEdit(doc)}>
                                    <Pencil className="mr-2 h-4 w-4" />
                                    Editar
                                  </DropdownMenuItem>
                                )}
                                {canShare(doc) && (
                                  <DropdownMenuItem onClick={() => handleOpenShare(doc)}>
                                    <Share2 className="mr-2 h-4 w-4" />
                                    Compartilhar
                                  </DropdownMenuItem>
                                )}
                                {compartilharNoEscritorio.podeCompartilhar && doc.origem === 'pessoal' && (
                                  <DropdownMenuItem
                                    onClick={() => {
                                      compartilharNoEscritorio.abrir('ARTEFATO', doc.id, doc.nome);
                                    }}
                                  >
                                    <Share2 className="mr-2 h-4 w-4" />
                                    Compartilhar no escritório…
                                  </DropdownMenuItem>
                                )}
                                {canWrite(doc) && (
                                  <>
                                    <DropdownMenuSeparator />
                                    {doc.cliente_id ? (
                                      <DropdownMenuItem onClick={() => handleUnlinkCliente(doc)}>
                                        <Users className="mr-2 h-4 w-4" />
                                        Desvincular cliente
                                      </DropdownMenuItem>
                                    ) : (
                                      <DropdownMenuItem onClick={() => handleOpenLinkCliente(doc)}>
                                        <Users className="mr-2 h-4 w-4" />
                                        Vincular cliente
                                      </DropdownMenuItem>
                                    )}
                                  </>
                                )}
                                {canWrite(doc) && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      className="text-destructive focus:text-destructive"
                                      onClick={() => handleOpenDelete(doc)}
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      Excluir
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="sm:p-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {filteredDocuments.map((doc) => (
                      <Card
                        key={doc.id}
                        className="cursor-pointer transition-all hover:border-primary/30 hover:shadow-md"
                        onClick={() => handlePreview(doc)}
                      >
                        <CardContent className="p-3">
                          <div className="flex items-start justify-between mb-3">
                            <FileTypeIcon mime={doc.mime} size="lg" />
                            <div className="flex min-w-0 items-center gap-1">
                            <TagsDoRegistro
                              recurso="artefatos"
                              registroId={doc.id}
                              identificador={doc.nome}
                              tags={doc.etiquetas}
                              noCache={tagsNoCache}
                              max={2}
                            />
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handlePreview(doc); }}>
                                  <Eye className="mr-2 h-4 w-4" />
                                  Visualizar
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDownload(doc); }}>
                                  <Download className="mr-2 h-4 w-4" />
                                  Download
                                </DropdownMenuItem>
                                {canWrite(doc) && (
                                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenEdit(doc); }}>
                                    <Pencil className="mr-2 h-4 w-4" />
                                    Editar
                                  </DropdownMenuItem>
                                )}
                                {canShare(doc) && (
                                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenShare(doc); }}>
                                    <Share2 className="mr-2 h-4 w-4" />
                                    Compartilhar
                                  </DropdownMenuItem>
                                )}
                                {compartilharNoEscritorio.podeCompartilhar && doc.origem === 'pessoal' && (
                                  <DropdownMenuItem
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      compartilharNoEscritorio.abrir('ARTEFATO', doc.id, doc.nome);
                                    }}
                                  >
                                    <Share2 className="mr-2 h-4 w-4" />
                                    Compartilhar no escritório…
                                  </DropdownMenuItem>
                                )}
                                {canWrite(doc) && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      className="text-destructive focus:text-destructive"
                                      onClick={(e) => { e.stopPropagation(); handleOpenDelete(doc); }}
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      Excluir
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                            </div>
                          </div>
                          <p className="mb-1 [overflow-wrap:anywhere] text-sm font-medium">{doc.nome}</p>
                          {(doc.descricao || (doc.origem && doc.origem !== 'pessoal')) && (
                            <div className="flex items-center gap-1.5 min-w-0 mb-1">
                              <OrigemBadge item={doc} />
                              {doc.descricao && (
                                <span className="min-w-0 shrink break-words text-xs text-muted-foreground">
                                  {doc.descricao}
                                </span>
                              )}
                            </div>
                          )}
                          <span className="text-xs text-muted-foreground">
                            {formatFileSize(doc.tamanho)}
                          </span>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

            {!isLoading && pagination.total > 0 && (
              <DataTablePagination
                page={pagination.page}
                pageSize={pagination.pageSize}
                total={pagination.total}
                pages={pagination.pages}
                onPageChange={pagination.goToPage}
                onPageSizeChange={pagination.changePageSize}
              />
            )}
        </Card>

        {/* Vincular Cliente Dialog */}
        <Dialog open={linkClienteDialogOpen} onOpenChange={setLinkClienteDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Vincular Cliente</DialogTitle>
              <DialogDescription>
                Selecione o cliente para vincular a este documento.
              </DialogDescription>
            </DialogHeader>
            <div className="py-2">
              <ClienteCombobox
                value={linkClienteValue}
                onChange={setLinkClienteValue}
              />
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancelar</Button>
              </DialogClose>
              <Button
                disabled={!linkClienteValue}
                onClick={handleConfirmLinkCliente}
              >
                Vincular
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Upload Dialog */}
        <UploadDocumentoDialog
          open={uploadDialogOpen}
          onOpenChange={setUploadDialogOpen}
        />

        {/* Preview Dialog */}
        <DocumentPreviewDialog
          open={previewDialogOpen}
          onOpenChange={setPreviewDialogOpen}
          document={selectedDocument}
          previewUrl={previewUrl}
          onDownload={() => selectedDocument && handleDownload(selectedDocument)}
          headerIcon={selectedDocument && <FileTypeIcon mime={selectedDocument.mime} />}
        />

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Editar Documento</DialogTitle>
              <DialogDescription>
                Altere os metadados do documento <strong>{selectedDocument?.nome}</strong>
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="edit-descricao">Descrição</Label>
                <Textarea
                  id="edit-descricao"
                  value={editDescricao}
                  onChange={(e) => setEditDescricao(e.target.value)}
                  placeholder="Descrição do documento"
                  rows={3}
                />
              </div>

              {selectedDocument && (
                <div className="space-y-1.5">
                  <Label>Tags</Label>
                  <p className="text-xs text-muted-foreground">As tags são aplicadas na hora, sem precisar salvar.</p>
                  <TagsDoRegistro
                    recurso="artefatos"
                    registroId={selectedDocument.id}
                    identificador={selectedDocument.nome}
                    tags={artefatos.find((a) => a.id === selectedDocument.id)?.etiquetas ?? selectedDocument.etiquetas}
                    noCache={tagsNoCache}
                    max={10}
                    className="flex"
                  />
                </div>
              )}
            </div>

            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancelar</Button>
              </DialogClose>
              <Button
                onClick={handleSaveEdit}
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Salvando...' : 'Salvar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Mover para lixeira</DialogTitle>
              <DialogDescription>
                Tem certeza que deseja mover o documento{' '}
                <strong>{selectedDocument?.nome}</strong> para a lixeira?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancelar</Button>
              </DialogClose>
              <Button
                variant="destructive"
                onClick={handleConfirmDelete}
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {deleteMutation.isPending ? 'Excluindo...' : 'Excluir'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Share Dialog */}
        {compartilharNoEscritorio.dialogo}
        <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Compartilhar Documento</DialogTitle>
              <DialogDescription>
                Compartilhe <strong>{selectedDocument?.nome}</strong> com outro usuário do JurisApp.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="E-mail do destinatário"
                  value={shareEmail}
                  onChange={(e) => setShareEmail(e.target.value)}
                  className="flex-1"
                />
                <Button
                  onClick={handleShare}
                  disabled={sharing || !shareEmail.trim()}
                  size="sm"
                >
                  {sharing ? 'Enviando...' : 'Compartilhar'}
                </Button>
              </div>
              {loadingShares ? (
                <div className="flex justify-center py-4">
                  <Spinner size={32} />
                </div>
              ) : shareList.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">Compartilhado com:</p>
                  {shareList.map((s) => (
                    <div key={s.id} className="flex items-center justify-between p-2 rounded border text-sm">
                      <div>
                        <p className="font-medium">{s.compartilhado_com_nome}</p>
                        <p className="text-xs text-muted-foreground">{s.compartilhado_com_email}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive"
                        onClick={() => handleRevokeShare(s.id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-2">
                  Documento ainda não foi compartilhado
                </p>
              )}
            </div>
          </DialogContent>
        </Dialog>
    </div>
  );
}

export default DocumentsPage;
